import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { useBugoHook } from '@shared/api/bugo/bugo.hook';
import { bugoStateSelectorFamily } from '@shared/api/bugo/bugo.selector';
import { shopItemForStoreStateSelectorFamily } from '@shared/api/shopItem/shotItem.selector';
import {
  createShopOrder,
  deleteShopOrderById,
} from '@shared/api/shopOrder/shopOrder.controller';
import {
  PaymentProvider,
  ShopOrder,
  ShopOrderCreateDto,
} from '@shared/api/shopOrder/shopOrder.interface';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { toastError } from '@shared/plugIn/toast';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole, UserRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatDate } from '@shared/utils/formatDate';
import { formatPrice } from '@shared/utils/formatPrice';
import { createPortal } from 'react-dom';
import { FieldErrors, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import PaymentResultOverlay from '../components/PaymentResultOverlay';
import PaymentWidgetModal from '../components/PaymentWidgetModal';
import PhraseInfoInputs from '../components/PhraseInfoInputs';
import ReceiverInfoInputs from '../components/ReceiverInfoInputs';
import SenderInfoInputs from '../components/SenderInfoInputs';
import ShopItemCounter from '../components/ShopItemCounter';
import { CustomerCallHelperBox } from '../containers/CustomerCallCenterBox/CustomerCallHelperBox';
import { useBootpayCheckout } from '../hooks/useBootpayCheckout';
import {
  TOSS_CHECKOUT_FORM_KEY,
  useShopTossCheckout,
} from '../hooks/useShopTossCheckout';
import { useStoreTitleHook } from '../hooks/useStoreTitleHook';
import { MAX_RETRY, useTossPaymentResult } from '../hooks/useTossPaymentResult';
import { IShopItemFormData, ShopItemFormData } from '../state/shopItemCheckout.interface';
import { composeReceiverAddress } from '../utils/composeReceiverAddress';
import { getEstimateDeliveredAt } from '../utils/getEstimateDeliveredAt';
import { getShopOrderDto } from '../utils/getShopOrderDto';

const ShopItemTossCheckoutPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 부고 정보
  const bugoId = searchParams.get('bugoId');
  useBugoHook(bugoId ?? '', !bugoId);
  const { data: bugoData } = useRecoilValue(bugoStateSelectorFamily(bugoId ?? ''));

  // 결제 제공자 상태 (폴백)
  const [provider, setProvider] = useState<PaymentProvider>('toss');
  const [showProviderFallbackModal, setShowProviderFallbackModal] = useState(false);

  // 주문 상태
  const [createdShopOrder, setCreatedShopOrder] = useState<ShopOrder | null>(null);
  const [isOrderCreating, setIsOrderCreating] = useState(false);

  useStoreTitleHook('주문/결제', ' ');
  const { storeRole, userProfile, signupOrLoginStoreGuest, ensureStoreCustomer, getMe } =
    useAuth();

  // 상품 정보
  const { itemId } = useParams();
  const { data: shopItemData, status: shopItemQueryStatus } = useRecoilValue(
    shopItemForStoreStateSelectorFamily(itemId ?? ''),
  );

  // Form
  const { register, control, handleSubmit, setValue, getValues, reset } =
    useForm<IShopItemFormData>();
  const itemCount = useWatch({
    control,
    name: ShopItemFormData.ItemCount,
    defaultValue: 1,
  });
  const usedPoint = useWatch({
    control,
    name: ShopItemFormData.UsedPoint,
    defaultValue: 0,
  });

  // 결제 금액 계산
  const totalAmount = shopItemData ? shopItemData.priceRetail * itemCount - usedPoint : 0;

  // 토스페이먼츠 결제 위젯 훅
  const {
    isReady,
    initError,
    isModalOpen: isPaymentWidgetModalOpen, // 결제위젯 모달 오픈상태
    isSubmitting,
    openModal: openPaymentWidgetModal,
    closeModal,
    requestModalPayment: requestPayment,
  } = useShopTossCheckout({ amount: totalAmount });

  // 부트페이 폴백 훅
  const bootpayCheckout = useBootpayCheckout();

  // Toss SDK 초기화 실패 → 자동 전환 (사용자 모름)
  useEffect(() => {
    if (initError) {
      setProvider('bootpay');
    }
  }, [initError]);

  // 재시도 한도 초과 시 새 결제 흐름 시작 ref (순환 참조 방지)
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onRestartPaymentRef = useRef<() => void>(() => {});
  const onRestartPaymentStable = useCallback(() => onRestartPaymentRef.current(), []);
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onProviderUnavailableRef = useRef<() => void>(() => {});
  const onProviderUnavailableStable = useCallback(
    () => onProviderUnavailableRef.current(),
    [],
  );
  const {
    isConfirming,
    confirmError,
    retryCount,
    handleRetry,
    handleCancelOrder,
    resetResult,
  } = useTossPaymentResult({
    bugoId,
    onRestartPayment: onRestartPaymentStable,
    onProviderUnavailable: onProviderUnavailableStable,
  });

  // 폼 데이터 sessionStorage 복원 (마운트 시 1회)
  useEffect(() => {
    const saved = sessionStorage.getItem(TOSS_CHECKOUT_FORM_KEY);
    if (saved) {
      try {
        const { itemId: savedItemId, data } = JSON.parse(saved);
        if (savedItemId === itemId) {
          reset(data);
        }
      } catch {
        // JSON 파싱 실패 시 무시
      }
      sessionStorage.removeItem(TOSS_CHECKOUT_FORM_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 부고 데이터로 수령인 정보 설정
  // 1회만 시딩 — 포커스 refetch로 bugoData가 갱신될 때 사용자가 수정한 값을 덮어쓰지 않도록
  const didSeedReceiverRef = useRef(false);
  useEffect(() => {
    if (!bugoData || didSeedReceiverRef.current) return;
    didSeedReceiverRef.current = true;
    // sessionStorage 복원값이 있으면 유지
    if (!getValues(ShopItemFormData.ReceiverAddress)) {
      setValue(ShopItemFormData.ReceiverAddress, composeReceiverAddress(bugoData));
    }
    if (!getValues(ShopItemFormData.ReceiverName)) {
      setValue(ShopItemFormData.ReceiverName, bugoData.member.fullName + ' 상주님');
    }
  }, [bugoData, setValue, getValues]);

  // 화환 수량은 1개 고정. 증감 시도 시 전화주문 안내로 유도
  const callHelperRef = useRef<HTMLDivElement>(null);
  const onItemCountLockedClick = useCallback(() => {
    toastError('2개 이상 주문은 전화로 도와드립니다.');
    callHelperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  // react-hook-form 유효성 검사 실패 핸들러
  const onFormError = useCallback((errors: FieldErrors<IShopItemFormData>) => {
    if (errors.senderName || errors.senderTel) {
      toastError('주문자 정보를 입력해주세요.');
    }
    if (errors.receiverName || errors.receiverAddress) {
      toastError('수령인 정보를 입력해주세요.');
    }
    if (errors.phraseSender) {
      toastError('리본문구를 입력해주세요.');
    }
  }, []);

  // 폼 제출 → 유효성 검사 → guest 가입 → 주문 생성 → 모달 열기
  const onSubmit: SubmitHandler<IShopItemFormData> = useCallback(async () => {
    if (isOrderCreating) return;

    const formData = getValues();

    if (!shopItemData || !bugoData) {
      toastError('상품 혹은 부고정보가 올바르지 않습니다.');
      return;
    }
    if (totalAmount <= 0) {
      toastError('결제 금액이 올바르지 않습니다.');
      return;
    }

    setIsOrderCreating(true);

    try {
      let userId = userProfile?._id;
      let role = storeRole();

      if (!userProfile || userProfile.roles.includes(UserRole.StoreGuest)) {
        // 이름/휴대폰번호 공백 제거 후 로그인·계정 생성
        // (예: "홍길동 " vs "홍길동"으로 별도 계정이 만들어지는 문제 방지)
        const normalizedName = String(formData.senderName).replace(/\s/g, '');
        // 전화번호는 숫자만 남김 (공백·하이픈 등 제거)
        const normalizedTel = String(formData.senderTel).replace(/[^0-9]/g, '');
        const token = await signupOrLoginStoreGuest(
          {
            name: normalizedName,
            phoneNumber: normalizedTel,
            password: normalizedTel.split('').slice(-4).join(''),
          },
          true,
        );
        const profile = await getMe(token);
        if (!profile?._id) return;
        userId = profile._id;
        role = profile.roles.includes(UserRole.StoreCustomer)
          ? LowerRole.StoreCustomer
          : LowerRole.StoreGuest;
      } else if (!userProfile.roles.includes(UserRole.StoreCustomer)) {
        // 로그인했지만 StoreCustomer가 아님(Admin/StoreSeller/Bugo* 등)
        // → StoreCustomer로 additive 승격(토큰·userProfile 갱신).
        //   storeRole()이 public으로 떨어져 주문이 막히는 것을 방어한다.
        const token = await ensureStoreCustomer(true);
        const profile = await getMe(token);
        if (!profile?._id) return;
        userId = profile._id;
        role = LowerRole.StoreCustomer;
      }

      if (!userId) return;

      const shopOrderDto: ShopOrderCreateDto = getShopOrderDto({
        formData,
        shopItem: shopItemData,
        bugo: bugoData,
        userId,
        provider,
      });
      const { data: shopOrder } = await createShopOrder(shopOrderDto, role);
      setCreatedShopOrder(shopOrder);

      if (provider === 'toss') {
        openPaymentWidgetModal();
      } else {
        await bootpayCheckout.requestPayment({
          shopOrder,
          shopItem: shopItemData,
          userProfile: userProfile!,
          formData,
          bugoId,
        });
      }
    } catch (err) {
      const message = (err as Error).message || '주문 처리 중 오류가 발생했습니다.';
      toastError(message);
    } finally {
      setIsOrderCreating(false);
    }
  }, [
    isOrderCreating,
    getValues,
    shopItemData,
    bugoData,
    totalAmount,
    userProfile,
    storeRole,
    signupOrLoginStoreGuest,
    ensureStoreCustomer,
    getMe,
    provider,
    openPaymentWidgetModal,
    bootpayCheckout,
    bugoId,
  ]);

  // 재시도 한도 초과 → 새 결제 흐름 시작 (latest ref 패턴)
  onRestartPaymentRef.current = () => {
    // overlay 닫기 + hook 상태 초기화
    resetResult();
    // URL 정리 (result/paymentKey 파라미터 제거)
    const cleanUrl = bugoId ? `?bugoId=${bugoId}` : window.location.pathname;
    navigate(cleanUrl, { replace: true });
    // 새 order 생성 → 위젯 모달 열기
    handleSubmit(onSubmit, onFormError)();
  };

  // PROVIDER_UNAVAILABLE → 부트페이 폴백 (latest ref 패턴)
  onProviderUnavailableRef.current = () => {
    setProvider('bootpay');
    resetResult();
    // URL 정리
    const cleanUrl = bugoId ? `?bugoId=${bugoId}` : window.location.pathname;
    navigate(cleanUrl, { replace: true });
    // 사용자에게 상황을 알리는 모달 표시
    setShowProviderFallbackModal(true);
  };

  // 폴백 모달 → 재결제 버튼 클릭
  const handleBootpayFallback = useCallback(() => {
    setShowProviderFallbackModal(false);
    handleSubmit(onSubmit, onFormError)();
  }, [handleSubmit, onSubmit, onFormError]);

  // 모달 결제 버튼 → requestModalPayment
  const onModalPayment = useCallback(async () => {
    if (!createdShopOrder || !shopItemData) return;
    const formData = getValues();

    await requestPayment({
      orderId: createdShopOrder._id,
      orderName: shopItemData.shopItemInfo.name,
      customerName: formData.senderName,
      customerMobilePhone: formData.senderTel,
      formData,
      shopItemId: shopItemData._id,
    });
  }, [createdShopOrder, shopItemData, getValues, requestPayment]);

  // 모달 닫기 → 주문 삭제 (사용자 명시적 취소)
  const onModalClose = useCallback(() => {
    if (createdShopOrder) {
      deleteShopOrderById(createdShopOrder._id, storeRole()).catch(() => {
        // 삭제 실패 시 서버 스케줄러가 정리
      });
    }
    setCreatedShopOrder(null);
    closeModal();
  }, [createdShopOrder, storeRole, closeModal]);

  // 모달 뒤로가기 처리 (history.pushState / popstate)
  const modalHistoryPushedRef = useRef(false);

  useEffect(() => {
    if (isPaymentWidgetModalOpen && !modalHistoryPushedRef.current) {
      modalHistoryPushedRef.current = true;
      history.pushState({ modal: true }, '');
    }

    if (!isPaymentWidgetModalOpen && modalHistoryPushedRef.current) {
      modalHistoryPushedRef.current = false;
      // popstate로 닫힌 경우가 아니면 history entry 제거
      if (history.state?.modal) {
        history.back();
      }
    }
  }, [isPaymentWidgetModalOpen]);

  useEffect(() => {
    const handlePopState = () => {
      if (modalHistoryPushedRef.current) {
        modalHistoryPushedRef.current = false;
        onModalClose();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [onModalClose]);

  return (
    <div>
      {searchParams.get('result') ? (
        // redirect 복귀 — PaymentResultOverlay가 처리할 때까지 로딩만 표시
        <div className="center-box h-screen-15">
          <Loading />
        </div>
      ) : shopItemQueryStatus === 'success' && shopItemData ? (
        <form onSubmit={handleSubmit(onSubmit, onFormError)} className="relative pb-12">
          <div className="p-4 text-sm">
            {/* 화환 정보 */}
            <div className="flex justify-between border-b border-gray-300 p-2 pt-0">
              <div className="flex">
                <img
                  src={fileBucketUrl(shopItemData.shopItemInfo.image.url, 'w400')}
                  alt="Flower Image"
                  className="aspect-square w-20 object-cover object-center"
                />
                <div className="pl-2">
                  <h4 className="font-bold">{shopItemData.shopItemInfo.name}</h4>
                  <div className="pt-1 text-xs theme-text-main">
                    {formatDate(getEstimateDeliveredAt(), {
                      dateSeparater: '/',
                      contains: {
                        year: false,
                        day: true,
                      },
                    })}
                    도착 보장
                  </div>
                  <div className="w-[84px] pt-4">
                    <ShopItemCounter
                      control={control}
                      name={ShopItemFormData.ItemCount}
                      locked
                      onLockedClick={onItemCountLockedClick}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-auto text-right">
                <div className="text-sm leading-4 text-gray-500 line-through">
                  {formatPrice(shopItemData.priceRetailShow * itemCount)}원
                </div>
                <div className="flex items-end">
                  <h3 className="text-xl font-bold leading-6">
                    {formatPrice(shopItemData.priceRetail * itemCount)}
                  </h3>
                  <span className="text-sm leading-6">원</span>
                </div>
              </div>
            </div>
            {/* Contents */}
            <div className="space-y-4 py-4">
              {/* 주문자 정보 */}
              <SenderInfoInputs
                register={register}
                control={control}
                names={{
                  senderName: ShopItemFormData.SenderName,
                  senderPassword: ShopItemFormData.SenderPassword,
                  senderTel: ShopItemFormData.SenderTel,
                }}
              />
              {/* 수령인 정보 */}
              <ReceiverInfoInputs
                register={register}
                control={control}
                names={{
                  receiverName: ShopItemFormData.ReceiverName,
                  receiverAddress: ShopItemFormData.ReceiverAddress,
                }}
              />
              {/* 문구 작성 */}
              <PhraseInfoInputs
                register={register}
                control={control}
                names={{
                  phraseSender: ShopItemFormData.PhraseSender,
                  phraseCondolence: ShopItemFormData.PhraseCondolence,
                }}
              />
              {/* 결제 정보 */}
              <div>
                <h4 className="mb-3 font-bold">결제 정보</h4>
                <div className="flex flex-col space-y-3 px-4 py-3 theme-bg-1">
                  <div className="flex items-center justify-between">
                    <span className="theme-text-8">상품 금액 ({itemCount}개)</span>
                    <span>{formatPrice(shopItemData.priceRetailShow * itemCount)}원</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="theme-text-8">할인 금액</span>
                    <span className="text-myTeal">
                      {formatPrice(
                        (shopItemData.priceRetail - shopItemData.priceRetailShow) *
                          itemCount,
                      )}
                      원
                    </span>
                  </div>
                  {usedPoint !== 0 && (
                    <div className="flex items-center justify-between">
                      <span className="theme-text-8">포인트사용</span>
                      <span className="text-myTeal">-{formatPrice(usedPoint)}원</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="theme-text-8">배송비</span>
                    <div className="flex items-center justify-end gap-x-2">
                      <div className="theme-text-main">
                        {formatDate(getEstimateDeliveredAt(), {
                          dateSeparater: '/',
                          contains: {
                            year: false,
                            day: true,
                          },
                        })}
                        도착 보장
                      </div>
                      <span>무료배송</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between border-t pt-3 theme-border-5">
                    <span className="font-bold">총 결제금액</span>
                    <span className="font-bold">{formatPrice(totalAmount)}원</span>
                  </div>
                </div>
              </div>
              {/* 동의 확인 */}
              <div>
                <div className="flex justify-between">
                  <h4 className="pb-3 font-bold">개인정보 취급방침에 대한 동의</h4>
                </div>
                <p className="p-4 pt-2 theme-text-8 theme-bg-1">
                  <span className="flex h-12 items-center justify-start py-2 text-sm">
                    아래 내용을 확인하였으며 결제에 동의합니다.
                  </span>
                  <span className="block font-bold leading-7 underline">
                    개인정보 취급방침
                  </span>
                  <span className="whitespace-pre-line break-keep text-xs leading-[18px]">
                    가온프라임(주)는 고객 만족을 위해 상품 품질과 배송 과정에 대해
                    책임지고 관리합니다.
                  </span>
                </p>
              </div>
              <div ref={callHelperRef}>
                <CustomerCallHelperBox />
              </div>
            </div>
          </div>
          {/* 결제 박스 */}
          <div className="fixed left-0 right-0 bottom-0 mx-auto flex max-w-screen-md items-center gap-x-6 border-t py-2 px-2 theme-bg-1 theme-border-5">
            <div className="pl-4 text-right text-xs">
              <div className="theme-text-8">총 할인금액</div>
              <div className="font-bold text-myTeal">
                {formatPrice(
                  -1 *
                    itemCount *
                    (shopItemData.priceRetailShow - shopItemData.priceRetail),
                )}
                원
              </div>
            </div>
            <Button
              type="submit"
              disabled={isOrderCreating}
              className="button-rectangle flex-1 text-white theme-bg-main disabled:opacity-50"
            >
              {isOrderCreating
                ? '주문 처리 중...'
                : `${formatPrice(totalAmount)}원 결제하기`}
            </Button>
          </div>
        </form>
      ) : (
        <div className="center-box h-screen-15">
          <Loading />
        </div>
      )}

      {/* 결제 결과 오버레이 */}
      <PaymentResultOverlay
        isConfirming={isConfirming}
        confirmError={confirmError}
        retryCount={retryCount}
        maxRetry={MAX_RETRY}
        onRetry={handleRetry}
        onCancel={handleCancelOrder}
      />

      {/* 결제 수단 선택 모달 (Toss only) */}
      <PaymentWidgetModal
        isOpen={isPaymentWidgetModalOpen}
        isReady={isReady}
        isSubmitting={isSubmitting}
        shopItem={shopItemData ?? null}
        itemCount={itemCount}
        totalAmount={totalAmount}
        onPay={onModalPayment}
        onClose={onModalClose}
      />

      {/* 결제 승인 오류 → 부트페이 재결제 모달 */}
      {showProviderFallbackModal &&
        createPortal(
          <div className="Preed">
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
                <div className="mb-4 flex justify-center">
                  <svg
                    className="h-12 w-12 text-orange-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h3 className="text-center text-lg font-bold">결제 승인 오류</h3>
                <p className="mt-2 text-center text-sm text-gray-500">
                  결제 처리 중 일시적인 오류가 발생했습니다.
                  <br />
                  다른 결제수단으로 다시 결제해 주세요.
                </p>
                <div className="mt-6">
                  <button
                    onClick={handleBootpayFallback}
                    disabled={!shopItemData || !bugoData}
                    className="w-full rounded-lg py-3 text-sm font-medium text-white theme-bg-main disabled:opacity-50"
                  >
                    {!shopItemData || !bugoData ? '준비 중...' : '재결제하기'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ShopItemTossCheckoutPage;
